import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Icon } from '../components/Icon.tsx';
import { HttpError, signup } from '../lib/api.ts';

// Same blurred-card backdrop as Login. Kept inline (not extracted) so each
// screen owns its own backdrop tweaks without cross-coupling.
const BG_CARDS = [
  { x: 80, y: 120, rot: -14, color: '#6b3fa0' },
  { x: 230, y: 60, rot: -6, color: '#0ea5e9' },
  { x: 1050, y: 70, rot: 9, color: '#db2777' },
  { x: 1200, y: 180, rot: 18, color: '#16a34a' },
  { x: 90, y: 540, rot: 12, color: '#facc15' },
  { x: 240, y: 640, rot: -4, color: '#dc2626' },
  { x: 1080, y: 620, rot: -16, color: '#22d3ee' },
  { x: 1230, y: 500, rot: 6, color: '#312e81' },
  { x: 470, y: -40, rot: 20, color: '#ea580c' },
  { x: 860, y: -20, rot: -22, color: '#15803d' },
];

export function Signup() {
  const [handle, setHandle] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const navigate = useNavigate();

  const handleTooShort = handle.trim().length === 0;
  const passwordTooShort = password.length < 8;
  const canSubmit = !busy && !handleTooShort && !passwordTooShort;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await signup(handle.trim(), password);
      // Server set the cookie; refresh /api/me so the auth gate lets us in.
      await qc.invalidateQueries({ queryKey: ['me'] });
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof HttpError) {
        if (err.status === 409) setError('That handle is taken. Try another.');
        else if (err.status === 429) setError('Too many attempts. Wait a few minutes.');
        else if (err.status === 400) setError('Check the handle (1-32 chars) and password (≥8).');
        else setError('Could not sign up. Please try again.');
      } else {
        setError('Could not reach the server.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="screen screen-desktop login-screen">
      <div className="login-bg">
        {BG_CARDS.map((c) => (
          <div
            key={`${c.x}-${c.y}`}
            className="bg-card"
            style={{
              left: c.x,
              top: c.y,
              transform: `rotate(${c.rot}deg)`,
              background: `linear-gradient(155deg, ${c.color}55, ${c.color}10 60%, #00000000)`,
            }}
          />
        ))}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'radial-gradient(600px 400px at 50% 50%, transparent, var(--bg) 75%)',
          }}
        />
      </div>

      <form className="login-card" onSubmit={submit}>
        <div className="login-brand">
          pokequant<span className="qmark">·</span>
        </div>
        <div className="login-tag">create an account</div>
        <input
          className="login-input"
          type="text"
          autoComplete="username"
          maxLength={32}
          placeholder="Handle (e.g. KB)"
          value={handle}
          onChange={(e) => setHandle(e.target.value)}
          disabled={busy}
        />
        <input
          className="login-input"
          type="password"
          autoComplete="new-password"
          maxLength={256}
          placeholder="Password (≥8 chars)"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={busy}
          style={{ marginTop: 8 }}
        />
        <button type="submit" className="login-btn" disabled={!canSubmit}>
          <span>{busy ? 'Creating…' : 'Create account'}</span>
          <Icon name="arrow" size={14} />
        </button>
        {error && (
          <div
            style={{
              marginTop: 8,
              fontSize: 11,
              color: 'var(--sell)',
              fontFamily: 'var(--font-mono)',
              textTransform: 'uppercase',
              letterSpacing: 0.04,
            }}
          >
            {error}
          </div>
        )}
        <div className="login-foot">
          <Link to="/login" style={{ color: 'var(--ink-dim)', textDecoration: 'none' }}>
            Already have an account? <span style={{ color: 'var(--ink)' }}>Sign in</span>
          </Link>
        </div>
      </form>
    </div>
  );
}
